import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, Tag, Spin } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useR5NP, ItemState } from './hooks/useR5NP';
import './R5NPForm.css';

// -- Helpers --
const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

// -- Component --
const R5NPForm: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');

  const {
    items,
    loading,
    executeLoading,
    queried,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleExecute,
  } = useR5NP();

  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onExecute = useCallback(() => {
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆客訴買收交運單？此操作將回沖庫存與金額。`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  const columns: ColumnsType<ItemState> = [
    {
      title: '沖銷',
      dataIndex: 'selected',
      key: 'selected',
      width: 60,
      align: 'center',
      render: (_: unknown, record: ItemState) => {
        if (record.validating) {
          return <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} />} />;
        }
        return (
          <Checkbox
            checked={record.selected}
            onChange={(e) =>
              handleToggleItem(record.item.fn210_01, e.target.checked)
            }
          />
        );
      },
    },
    {
      title: '買收單號',
      dataIndex: ['item', 'fn210_01'],
      key: 'fn210_01',
      width: 120,
      render: (text: string, record: ItemState) => (
        <span>
          {text}
          {record.valid === true && (
            <CheckCircleOutlined className="status-valid" style={{ marginLeft: 6 }} />
          )}
          {record.valid === false && (
            <CloseCircleOutlined className="status-invalid" style={{ marginLeft: 6 }} title={record.validationMsg} />
          )}
        </span>
      ),
    },
    {
      title: '交運日期',
      dataIndex: ['item', 'fn210_11'],
      key: 'fn210_11',
      width: 120,
      render: (v: string | null) => fmtDate(v),
    },
    {
      title: '廠商代號',
      dataIndex: ['item', 'fn210_12'],
      key: 'fn210_12',
      width: 120,
    },
    {
      title: '廠商名稱',
      dataIndex: ['item', 'vendor_name'],
      key: 'vendor_name',
      width: 200,
    },
    {
      title: '狀態',
      key: 'status',
      width: 80,
      align: 'center',
      render: (_: unknown, record: ItemState) => {
        if (record.valid === true) return <Tag color="success">可沖銷</Tag>;
        if (record.valid === false) return <Tag color="error">不可沖</Tag>;
        return <Tag>待驗證</Tag>;
      },
    },
  ];

  return (
    <div className="r5np-form">
      {(loading || executeLoading) && (
        <div className="loading-overlay">
          {executeLoading ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>客訴管理</a>
              <span>/</span>
              <span className="current">客訴買收交運單核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>客訴買收交運單核准</h1>
            <span className="badge">R5NP</span>
          </div>
        </div>

        <div className="query-bar">
          <label>買收單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 180 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 180 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading}>
            確定範圍
          </Button>
        </div>

        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>，已勾選 <strong>{selectedCount}</strong> 筆</span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fn210_01}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入買收單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executeLoading}
            loading={executeLoading}
            onClick={onExecute}
          >
            存檔 ({selectedCount})
          </Button>
          <Button danger size="large" onClick={() => navigate('/erp')}>
            離開
          </Button>
        </div>
      </div>
    </div>
  );
};

export default R5NPForm;
